import React, { useEffect, useState } from "react";
import {
  Layout,
  HeaderPage,
  Tile,
  IconTypography,
  Keyline,
  FileInput,
  GutterContainer,
  LinkText
} from "@common/AggregationComponent";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  checkPreviousPageExists,
  transformBrandForGoogleAnalytics,
  getBrandForMeta
} from "@util/common";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import imageOnLoad from "@util/uploadImage";
import { sendTrackEvent, sendTrackPageView, TRACK_EVENT } from "@util/gtm";
import { Helmet } from "react-helmet-async";
import type { CombinedReducersType } from "@type/index";
import { AppActions, ImageSearchActions } from "@redux/reducer";
import withBreadcrumb from "../../hoc/withBreadcrumb";

import UploadServerErrorModel from "../../common/UploadServerErrorModel";

const ImageSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [cookies, setCookie] = useCookies(["tagName"]);
  const [allowFileTypes] = useState<string[]>([
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif"
  ]);
  const config = useSelector((store: CombinedReducersType) => store.app);
  const BASE_PATH_NAME = `/${config.region}/${config.language}`;

  useEffect(() => {
    dispatch(ImageSearchActions.clearSearchImage());
  });

  useEffect(() => {
    sendTrackPageView({
      event: "trackPageView",
      brand: transformBrandForGoogleAnalytics(config.brand_name), // UQの場合"uq"
      pageType: "imagesearch", // TPUの場合TPU
      subID: "", // subid取得できる場合subidを出力、できない場合undefined
      pageUrlPath: `${location.pathname}${location.search}`, // path+param
      pageTitle: t("text_image_search_meta_title"), // title
      previousPageExists: checkPreviousPageExists() // 前のページがある場合true、ない場合false
    });
  }, [location]);

  // input Change
  const setFileData = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // clear session storage of history
      sessionStorage.removeItem("productScrollTop");
      sessionStorage.removeItem("outfitScrollTop");
      // clear tagName in cookies
      if (cookies.tagName) {
        setCookie("tagName", "", { path: "/" });
      }
      sendTrackEvent({
        brand: transformBrandForGoogleAnalytics(config.brand_name),
        event: TRACK_EVENT,
        eventAction: "ExecuteImageSearch",
        eventCategory: "IS_Top",
        eventLabel: ""
      });
      const imgFile = e.target.files[0];
      const typeFlag = allowFileTypes.find(
        (value: any) => value === imgFile?.type
      );
      if (typeFlag) {
        imageOnLoad(imgFile).then((res: any) => {
          dispatch(ImageSearchActions.setSearchImage(res));
          dispatch(ImageSearchActions.setClickUploadImage(true));
          dispatch(ImageSearchActions.setUploadImageLoading(true));
          navigate(`${BASE_PATH_NAME}/image-search/search/product`);
        });
      } else {
        dispatch(AppActions.setUploadServerErrorModal(true));
      }
      const inputEle: any = document.querySelector("input[type=file]");
      if (inputEle) {
        inputEle.value = null;
      }
    }
  };
  return (
    <>
      <Helmet
        titleTemplate={t("text_image_search_meta_title")}
        defaultTitle={t("text_image_search_meta_title")}
        htmlAttributes={{ lang: config.language }}
      >
        <meta
          name="description"
          content={t("text_image_search_result_meta_description")}
        />
        <meta property="og:site_name" content={getBrandForMeta()} />
        <meta property="og:title" content={t("text_image_search_meta_title")} />
        <meta property="og:description" content={""} />
        <meta property="og:image" content={""} />
        <meta property="fb:app_id" content={""} />
        <meta name="twitter:card" content={"summary"} />
        <meta name="twitter:site" content={getBrandForMeta()} />
        <meta
          name="twitter:title"
          content={t("text_image_search_meta_title")}
        />
        <meta name="twitter:image" content={""} />
        <meta
          property="og:url"
          content={`${window.origin}${BASE_PATH_NAME}/image-search`}
        />
      </Helmet>
      <Layout
        colSpan={{
          lg: 12,
          md: 12,
          sm: 4
        }}
        element="div"
      >
        <HeaderPage
          hideKeyline
          captions={[`${t("text_image_search_message_01")}`]}
          errors={[]}
          labelElement="h1"
          textLink={undefined}
          title={t("text_image_search")}
          toolTip={undefined}
        />
        <Keyline className="fr-ec-mb-spacing-06" />
        <Layout
          element="div"
          colSpan={{ sm: 4, md: 6, lg: 4 }}
          gutter={{ sm: true, md: false, lg: false }}
          className="fr-ec-mb-spacing-05"
        >
          <FileInput
            accept="image/jpg,image/jpeg,image/png,image/webp,image/gif"
            icon="visual-search"
            label={t("text_image_search_image_upload_button")}
            variant="button"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFileData(e)
            }
          />
        </Layout>
        {!config.isApp ? (
          <GutterContainer>
            <Tile
              className="fr-ec-mb-spacing-08"
              element="a"
              href={`${BASE_PATH_NAME}/search`}
            >
              <IconTypography icon="search">
                <LinkText
                  ariaLabel="aria Label"
                  element="a"
                  variant="standalone-secondary"
                  className="fr-ec-mb-spacing-02"
                >
                  {t("text_image_search_searchbykeyword")}
                </LinkText>
              </IconTypography>
            </Tile>
          </GutterContainer>
        ) : (
          <></>
        )}
        {config.isVisibleUploadErrorModal && <UploadServerErrorModel />}
      </Layout>
    </>
  );
};
export default withBreadcrumb(ImageSearch, { toggleMinHeight: true });
